<template>
  <section class="contact-cards-module-component">
    <div
      v-for="(card, index) in props.data.cards"
      :key="index"
      class="cards">
      <div class="card">
        <div class="inner-card">
          <div class="front-card">
            <div class="pt-1">
              <div
                class="front-content"
                v-html="getFrontContentWithDivider(card)"></div>

              <Vue3SlideUpDown
                v-if="card.add_more"
                :model-value="isCardActive(index)">
                <div class="back-card">
                  <div
                    class="details-content"
                    v-html="card.information?.content"></div>

                  <div
                    v-if="getUnderlinedInformation(card).length"
                    class="underlined-information">
                    <div
                      v-for="(item, itemIndex) in getUnderlinedInformation(
                        card
                      )"
                      :key="`underlined-${index}-${itemIndex}`"
                      class="underlined-item"
                      :class="{
                        small: isSmallTextEnabled(card),
                      }"
                      v-html="item"></div>
                  </div>
                </div>
              </Vue3SlideUpDown>
            </div>

            <div class="bottom">
              <a
                v-if="card.button && (!card.add_more || isCardActive(index))"
                class="contact-button"
                :href="card.button.url"
                :target="card.button.target"
                :title="card.button.title"
                v-html="card.button.title"></a>

              <button
                v-if="card.add_more"
                type="button"
                class="trigger"
                :class="{ open: isCardActive(index) }"
                @click="toggleCardActive(index)">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="8"
                  viewBox="0 0 15 8"
                  fill="none">
                  <path
                    d="M7.5 5.56777C7.72059 5.56777 7.7521 5.53846 8.0042 5.27473L13.6765 0H14.1176L15 0.820513V1.23077L7.72059 8H7.27941L0 1.26007V0.849817L0.882353 0.029304H1.32353L6.9958 5.30403C7.2479 5.53846 7.31092 5.59707 7.5 5.59707V5.56777Z"
                    fill="#0E4343" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "ContactCardsComponent",
  "CLASSNAME": "contact-cards-module-component",
  "URL": "http://vuevitewp.local/test"
 }
#ENDMETA */

/*
 * Insert your logic here
 */

import { ref } from 'vue'
import Vue3SlideUpDown from 'vue3-slide-up-down'

const props = defineProps(['data'])
const activeCards = ref({})

const enabled = (value) => value === true || value === 1 || value === '1'

const isSmallTextEnabled = (card) => {
  const info = card?.information
  const value = info?.make_text_small ?? info?.makeTextSmall

  return enabled(value)
}

const getCardKey = (index) => `${index}`

const isCardActive = (index) => Boolean(activeCards.value[getCardKey(index)])

const toggleCardActive = (index) => {
  const key = getCardKey(index)

  activeCards.value = {
    ...activeCards.value,
    [key]: !activeCards.value[key],
  }
}

const getUnderlinedInformation = (card) => {
  const items =
    card?.information?.underlined_information ||
    card?.information?.underlinedInformation ||
    []

  if (!Array.isArray(items)) return []

  return items
    .map((item) => item?.information)
    .filter((item) => typeof item === 'string' && item.trim())
}

const getFrontContentWithDivider = (card) => {
  const content = card?.content
  if (typeof content !== 'string' || !content.trim()) return ''

  const divider = '<div class="front-more-divider" aria-hidden="true"></div>'

  return content
    .replace(/<p>\s*<!--more-->\s*<\/p>/gi, divider)
    .replace(/<!--more-->/gi, divider)
}
</script>

<style lang="scss" scoped>
.contact-cards-module-component {
  @apply w-full max-w-screen-xl mx-auto py-8 md:py-10 px-6 md:px-0;
  @apply grid grid-cols-1 md:grid-cols-2 gap-5 xl:grid-cols-1 xl:pt-0 xl:gap-[50px];

  .cards {
    @apply w-full;

    .card {
      @apply bg-white rounded-[20px] p-6 overflow-hidden h-full;

      box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.25);

      .inner-card {
        @apply relative h-full;

        .front-card {
          @apply flex flex-col justify-between h-full;

          .front-content {
            @apply opacity-100;

            :deep(.front-more-divider) {
              @apply w-full mb-3 pb-1 border-b border-progressive;
            }

            :deep(h3) {
              @apply header-4 mb-3;
            }

            :deep(p) {
              @apply text-[15px] leading-[17px] mb-3;
            }

            :deep(strong) {
              @apply font-medium;
            }
          }

          .back-card {
            @apply pt-2 mb-4;

            .underlined-information {
              @apply w-full flex flex-col pt-5 gap-3;

              .underlined-item {
                @apply w-full pb-2 border-b border-progressive flex flex-col gap-4 lg:gap-6;
              }
            }

            .details-content {
              :deep(*) {
                @apply text-[16px] leading-[22px] mb-3;

                strong {
                  @apply text-balanced;
                }
              }
            }

            .underlined-information {
              .underlined-item {
                :deep(h3) {
                  @apply body text-balanced;
                }

                :deep(p) {
                  @apply body text-balanced;
                }

                :deep(a) {
                  @apply body text-balanced underline;
                }

                :deep(strong) {
                  @apply font-medium;
                }

                &.small {
                  :deep(h3) {
                    @apply header-4;
                  }

                  :deep(p) {
                    @apply text-[15px] leading-[17px];
                  }

                  &:deep(a) {
                    @apply text-[15px] leading-[17px] mb-3;
                  }

                  :deep(strong) {
                    @apply font-medium;
                  }
                }
              }
            }
          }

          .bottom {
            @apply flex items-center justify-between gap-3 mt-2;

            .contact-button {
              @apply bg-balanced text-progressive px-3 py-[6px] rounded-[20px] text-[16px] transition-all;
              @apply hover:bg-progressive hover:text-balanced;
            }

            .trigger {
              @apply ml-auto flex-[1_0_auto] max-w-[35px] h-[35px] w-[35px] flex justify-center items-center rounded-full border border-balanced;

              &.open {
                svg {
                  @apply rotate-180 transition-transform duration-500;
                }
              }

              svg {
                @apply transition-transform duration-500;
              }
            }
          }
        }
      }
    }
  }
}
</style>
